import { Lightning, Log } from '@lightningjs/sdk';
import { RecordingFolderDataSource } from '../../../Business/recording/RecordingFolderDataSource';
import { Button } from '../../../components/shared/Button';
import { RecordingTranslationKeys } from '../../../api/models/TranslationKeys';
import { AdultType } from '../../../api/models/Shared.types';
import { _constants } from '../../../components/MiniDetail';
import { CmsConstants } from '../../../Business/cms/CmsConstants';
import { ChannelManager } from '../../../api/ChannelManager';
import { CmsGrid } from '../../../components/cms/CmsGrid';
import { SubscriptionTypes } from '../../../Business/detail/actions/epgRecordingActions/SubscriptionSettings.types';

const Constants = {
  button: {
    margin: {
      left: CmsConstants.Ribbon.pageContentOffset,
      top: 56,
    },
    height: 60,
    width: 200,
  },
};

export class RecordingFolderPage extends Lightning.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      clipping: true,
      w: w => w,
      h: h => h,
      Container: {
        w: w => w,
        DeleteAll: {
          x: Constants.button.margin.left,
          y: Constants.button.margin.top,
          type: Button,
          buttonMode: 'primary',
          signals: {
            onActivate: '_handleDelete',
          },
        },
        Content: {
          flex: { direction: 'column' },
          w: w => w,
          y: Constants.button.margin.top + Constants.button.height + 10,
        },
      },
    };
  }

  _init() {
    /**
     * @type {Backend}
     */

    const backend = this.params.backend;
    const folderInfo = this.params.folderInfo;
    this.tag('DeleteAll').label = backend.translations.forKey(RecordingTranslationKeys.recordings_delete_all);
    Promise.all([
      ChannelManager.channels(backend),
      backend.cachedRecordingsApi.recordingsFolderForType(
        backend,
        folderInfo.pageType,
        AdultType.False,
        folderInfo.seriesId,
        folderInfo.channelId
      ),
    ]).then(data => {
      this.ds = new RecordingFolderDataSource(backend, this, data[1], data[0], folderInfo);
      const grids = this.ds.getRecordingContent();
      if (!grids) {
        return;
      }
      const content = this.tag('Content');
      Promise.all(grids).then(sources => {
        sources.forEach(grid => {
          content.childList.a({
            flexItem: { marginBottom: 20 },
            signals: { centerVertical: '_centerVertical' },
            w: this.finalW,
            x: CmsConstants.Ribbon.pageContentOffset,
            ...grid.componentDescription(),
          });
        });
        this.focusIndex = content.childList.length > 0 ? 0 : -1;
        this._refocus();
      });
    });
  }

  _getFocused() {
    if (this.focusIndex !== -1) {
      return this.tag('Content').children[this.focusIndex];
    } else {
      return this.tag('DeleteAll');
    }
  }

  _centerVertical(centerY) {
    // Workaround for modals
    // Showing/hiding a modal will cause a refocus event, at which point this.finalH === 0
    // Prevent recentering in that case
    if (this.finalH === 0) return;

    let target = this.finalH / 2;

    if (this.tag('Content').children[this.focusIndex].data.cmsProperties) {
      if (!this.tag('Content').children[this.focusIndex].data.cmsProperties.includes('HIDE_MINI_DETAIL')) {
        target = (this.finalH - _constants.miniDetailHeight) / 2;
      }
    }

    const result = Math.min(
      0, //-this.tag('Content').finalH + this.tag('Content').finalH - constants.marginBottom,
      target - centerY
    );
    // console.log('centerVertical', centerY, target, result, this.tag('Content').finalH);

    this.tag('Container').setSmooth('y', result);
  }

  _handleDown() {
    if (this.focusIndex < this.tag('Content').children.length - 1) {
      this.focusIndex++;
    }
  }

  _handleUp() {
    if (this.focusIndex >= 0) {
      this.focusIndex--;
    }
  }

  _handleDelete() {
    this.ds.handleDelete();
  }

  _handleBack() {
    this.ds.returnToPreviousPage();
  }

  /**
   * TODO maybe merge recordingPage and RecordingFolderPage into one page?
   * @param cardEl {Lightning.Component}
   * @param type {SubscriptionTypes}
   */
  $removeEpgElement(cardEl, type) {
    /** @type {CmsGrid} */
    const grid = cardEl.cparent;
    if (grid?.cparent !== this) {
      Log.error(
        '[RecordingFolderPage]',
        'received remove call for card whose grid does not belong to this page, returning'
      );
      return;
    }
    if (type === SubscriptionTypes.PROGRAM) {
      // remove the one
      const remaining = grid.removeCard(cardEl);
      // check if no more items and delete entire grid
      if (remaining === 0) {
        this._deleteSeason(grid);
      }
    } else if (type === SubscriptionTypes.SEASON) {
      // clear grid
      this._deleteSeason(grid);
    } else if (type === SubscriptionTypes.SERIES) {
      // signal to page to delete everything
      this._deleteSeries();
    }

    this.fireAncestors('$hideMiniDetail');
    this._refocus();
  }

  /**
   * @param {CmsGrid} seasonGrid
   */
  _deleteSeason(seasonGrid) {
    const content = this.tag('Content');
    content.childList.remove(seasonGrid);
    // check if index still exists else pick new one
    this.focusIndex = Math.min(this.focusIndex, content.childList.length - 1);
    if (content.childList.length === 0) {
      // if there are no season grids left, return to recording page
      this.ds.returnToPrevious(); // rename to returnToPreviousPage when merge conflict
      return;
    }
    // recenter content
    this._refocus();
  }

  _deleteSeries() {
    this.ds.forceDelete();
  }
}
